import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { CommandItem } from "../../components/Editor/SlashExtension";
import { CombinedSelectedFilters } from "../../features/SearchFilters/utils";
import { SearchFilters } from "../../services/search/searchFilterService";
import {
  ISearchFilter,
  ISearchResultSortingModes,
  QUERY_PARAMS,
  ICompositeResult,
  SearchFilterSubType,
  PaginatedSearchResultInfo,
  ResultOffsetType,
  FeedbackIds,
  AptAnswersSearchContextItem,
  SearchResultLoadingByQuestion,
  ArticleGeneratedByQuestion,
  NextStep
} from "../../types/entities";
import { isSupportApp } from "../../utils/supportApp";

const SEARCH_PATH = isSupportApp
  ? `?${window.location.hash.split("?")[1]}`
  : window.location.search;

const queryParams = new URLSearchParams(SEARCH_PATH);

const INITIAL_ANSWER_ID = queryParams.get(QUERY_PARAMS.ANSWER_ID) ?? "";
const INITIAL_RESULT_ID = queryParams.get(QUERY_PARAMS.RESULT_ID) ?? "";

type SliceStateType = {
  selectedSearchResult: ICompositeResult;
  selectedSearchResultsQuestion: number;
  selectedSearchResultsQuestionText: string;
  searchCardVisibility: boolean;
  searchResults: ICompositeResult[];
  searchResultsByQuestion: Record<string, ICompositeResult[]>;
  searchResultsExpanded: ICompositeResult[];
  searchResultsInfo?: PaginatedSearchResultInfo;
  isPreviewDataLoading: boolean;
  isSearchFocused: boolean;
  isSearchLoading: boolean;
  searchQuery: string;
  page: number;
  searchFilter: ISearchFilter;
  searchFilterSubType: SearchFilterSubType;
  searchSuggestions: string[];
  sortMode: ISearchResultSortingModes;
  showSuggestion: boolean;
  hasLinkedEdge: boolean;
  totalSearchResults: number;
  autoSearchActive: boolean;
  filters: SearchFilters[];
  // Legacy (non-unified) web UI only. Everything else reads useSavedMyProducts, which cannot
  // go stale; don't read this in new code.
  myProducts: AptAnswersSearchContextItem[];
  productFilters: AptAnswersSearchContextItem[];
  quickFilters: AptAnswersSearchContextItem[];
  searchResultVisibilityOnEmbeddedSearch: boolean;
  answerId: string;
  regenerateAnswerId?: string;
  regenerateQuestionIndex?: number;
  // Set when the user changes the products of an asked question: its re-run is asked with
  // exactly their choice, so products aren't picked for it. Cleared with the regenerate fields.
  skipAutoProductFilters: boolean;
  searchResultOffsets: ResultOffsetType[];
  showBackButtonInPreview: boolean;
  feedbackSubmittedIds: FeedbackIds;
  initialSearchQuery: boolean;
  pageHtml: string;
  previousPageHtml: string;
  resultId: string;
  answerStreamingStarted: boolean;
  productFiltersList: AptAnswersSearchContextItem[];
  quickFiltersList: AptAnswersSearchContextItem[];
  selectedFilters: CombinedSelectedFilters[];
  searchResultLoadingByQuestion: SearchResultLoadingByQuestion;
  isArticleGeneratedByQuestion: ArticleGeneratedByQuestion;
  followupsByQuestion: Record<string, NextStep[]>;
  skills: CommandItem[];
};
const initialState: SliceStateType = {
  selectedSearchResult: {} as ICompositeResult,
  selectedSearchResultsQuestion: 0,
  selectedSearchResultsQuestionText: "",
  searchCardVisibility: false,
  searchResults: [],
  searchResultsByQuestion: {},
  searchResultsExpanded: [],
  searchResultsInfo: {} as PaginatedSearchResultInfo,
  isPreviewDataLoading: false,
  isSearchFocused: true,
  isSearchLoading: false,
  searchQuery: "",
  page: 1,
  searchFilter: ISearchFilter.ALL,
  searchSuggestions: [],
  searchFilterSubType: "",
  sortMode: ISearchResultSortingModes.RELEVANCE,

  showSuggestion: false,
  hasLinkedEdge: false,
  filters: [],
  quickFilters: [],
  productFilters: [],
  myProducts: [],
  totalSearchResults: 0,
  autoSearchActive: false,
  searchResultVisibilityOnEmbeddedSearch: false,
  answerId: INITIAL_ANSWER_ID,
  skipAutoProductFilters: false,
  searchResultOffsets: [] as ResultOffsetType[],
  showBackButtonInPreview: false,
  feedbackSubmittedIds: {} as FeedbackIds,
  initialSearchQuery: true,
  pageHtml: "",
  previousPageHtml: "",
  resultId: INITIAL_RESULT_ID,
  answerStreamingStarted: false,
  productFiltersList: [] as AptAnswersSearchContextItem[],
  quickFiltersList: [] as AptAnswersSearchContextItem[],
  selectedFilters: [] as CombinedSelectedFilters[],
  searchResultLoadingByQuestion: {} as SearchResultLoadingByQuestion,
  isArticleGeneratedByQuestion: {} as ArticleGeneratedByQuestion,
  followupsByQuestion: {},
  skills: [] as CommandItem[]
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    updateSelectedSearchResult: (
      state,
      action: PayloadAction<ICompositeResult>
    ) => {
      state.selectedSearchResult = action.payload;
      state.searchCardVisibility = true;
    },
    updateSelectedSearchResultsQuestion: (
      state,
      action: PayloadAction<number>
    ) => {
      state.selectedSearchResultsQuestion = action.payload;
    },
    updateSelectedSearchResultsQuestionText: (
      state,
      action: PayloadAction<string>
    ) => {
      state.selectedSearchResultsQuestionText = action.payload;
      state.searchResultsByQuestion[action.payload] = state.searchResults;
    },
    updateSearchCardVisibility: (state, action) => {
      state.searchCardVisibility = action.payload;
      state.selectedSearchResult = {} as ICompositeResult;
    },
    updateSearchFilters: (state, action) => {
      state.filters = action.payload;
    },

    updateSearchResults: (state, action: PayloadAction<ICompositeResult[]>) => {
      state.searchResults = action.payload;
      state.searchResultsByQuestion[state.selectedSearchResultsQuestionText] =
        action.payload;
      state.searchSuggestions = [];
    },
    updateSearchResultByQuestion: (
      state,
      action: PayloadAction<{ query: string; results: ICompositeResult[] }>
    ) => {
      state.searchResultsByQuestion[action.payload.query] =
        action.payload.results;
    },
    setSearchResultsByQuestion: (
      state,
      action: PayloadAction<Record<string, ICompositeResult[]>>
    ) => {
      state.searchResultsByQuestion = action.payload;
    },
    addSearchResults: (state, action: PayloadAction<ICompositeResult[]>) => {
      state.searchResults = action.payload;
    },
    updateSearchResultsInfo: (state, action) => {
      state.searchResultsInfo = action.payload;
    },
    updateSearchResultsExpanded: (state, action) => {
      state.searchResultsExpanded = action.payload;
    },
    setIsSearchFocused: (state, action) => {
      state.isSearchFocused = action.payload;
    },
    setIsSearchLoading: (state, action) => {
      state.isSearchLoading = action.payload;
    },
    setIsPreviewDataLoadingState: (state, action) => {
      state.isPreviewDataLoading = action.payload;
    },
    setInitialSearchQuery: (state, action) => {
      state.initialSearchQuery = action.payload;
    },
    updateSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      state.initialSearchQuery = false;
    },
    updatePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    updateSearchFilter: (state, action: PayloadAction<ISearchFilter>) => {
      state.searchFilter = action.payload;
    },
    updateQuickFilters: (
      state,
      action: PayloadAction<AptAnswersSearchContextItem[]>
    ) => {
      state.quickFilters = action.payload;
    },
    updateProductFilters: (
      state,
      action: PayloadAction<AptAnswersSearchContextItem[]>
    ) => {
      state.productFilters = action.payload;
    },
    updateMyProducts: (
      state,
      action: PayloadAction<AptAnswersSearchContextItem[]>
    ) => {
      state.myProducts = action.payload;
    },
    updateSearchFilterSubType: (
      state,
      action: PayloadAction<SearchFilterSubType>
    ) => {
      state.searchFilterSubType = action.payload;
    },
    updateSearchSuggestions: (state, action: PayloadAction<string[]>) => {
      state.searchSuggestions = action.payload;
    },
    updateShowSuggestion: (state, action: PayloadAction<boolean>) => {
      state.showSuggestion = action.payload;
    },
    updateHasLinkedEdge: (state, action: PayloadAction<boolean>) => {
      state.hasLinkedEdge = action.payload;
    },
    resetSearch: (state) => {
      state.hasLinkedEdge = false;
      state.searchQuery = "";
      // TODO: The FE code uses "" to indicate no filtering, while the BE uses "all" to indicate
      // lack of filtering. This, obviously, leads to conflicts and confusion.
      state.searchFilter = ISearchFilter.NO_ALL;
      state.searchFilterSubType = "";
      state.searchResults = [];
      state.searchResultsExpanded = [];
      state.searchResultsByQuestion = {};
      state.totalSearchResults = 0;
      state.autoSearchActive = false;
      state.isSearchLoading = false;
      state.searchResultOffsets = [] as ResultOffsetType[];
      state.page = 1;
      state.answerId = "";
      state.searchResultsInfo = {} as PaginatedSearchResultInfo;
      state.showBackButtonInPreview = false;
      state.feedbackSubmittedIds = [];
      state.productFilters = [];
      state.quickFilters = [];
      state.resultId = "";
      state.selectedFilters = [] as CombinedSelectedFilters[];
      state.regenerateAnswerId = undefined;
      state.regenerateQuestionIndex = undefined;
      state.skipAutoProductFilters = false;
      state.searchResultLoadingByQuestion = {} as SearchResultLoadingByQuestion;
      state.isArticleGeneratedByQuestion = {} as ArticleGeneratedByQuestion;
      state.followupsByQuestion = {};
    },
    updateSortMode: (
      state,
      action: PayloadAction<ISearchResultSortingModes>
    ) => {
      state.sortMode = action.payload;
    },
    // TODO: remove this
    updateTotalSearchResults: (state, action: PayloadAction<number>) => {
      state.totalSearchResults = action.payload;
    },
    updateAutoSearchActive: (state, action: PayloadAction<boolean>) => {
      state.autoSearchActive = action.payload;
    },
    updateSearchResultVisibilityOnEmbeddedApp: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.searchResultVisibilityOnEmbeddedSearch = action.payload;
    },
    updateAnswerId: (state, action: PayloadAction<string>) => {
      state.answerId = action.payload;
      state.searchResults = [];
      state.searchResultsByQuestion = {};
    },
    resetRegenerateAnswerId: (state) => {
      state.regenerateAnswerId = undefined;
      state.regenerateQuestionIndex = undefined;
      state.skipAutoProductFilters = false;
    },
    setSkipAutoProductFilters: (state, action: PayloadAction<boolean>) => {
      state.skipAutoProductFilters = action.payload;
    },
    updateRegenerateAnswerId: (state, action: PayloadAction<string>) => {
      state.regenerateAnswerId = action.payload;
    },
    updateRegenerateQuestionIndex: (state, action: PayloadAction<number>) => {
      state.regenerateQuestionIndex = action.payload;
    },
    updateSearchResultOffsets: (
      state,
      action: PayloadAction<ResultOffsetType[]>
    ) => {
      state.searchResultOffsets = action.payload;
    },
    updateShowBackButtonInPreview: (state, action: PayloadAction<boolean>) => {
      state.showBackButtonInPreview = action.payload;
    },
    updateFeedbackSubmittedIds: (state, action: PayloadAction<FeedbackIds>) => {
      state.feedbackSubmittedIds = action.payload;
    },
    updatePageHtml: (state, action: PayloadAction<string>) => {
      state.pageHtml = action.payload;
    },
    updatePreviousPageHtml: (state, action: PayloadAction<string>) => {
      state.previousPageHtml = action.payload;
    },
    updateResultId: (state, action: PayloadAction<string>) => {
      state.resultId = action.payload;
    },
    setAnswerStreamingStarted: (state, action: PayloadAction<boolean>) => {
      state.answerStreamingStarted = action.payload;
    },
    updateProductFiltersList: (
      state,
      action: PayloadAction<AptAnswersSearchContextItem[]>
    ) => {
      state.productFiltersList = action.payload;
    },
    updateQuickFiltersList: (
      state,
      action: PayloadAction<AptAnswersSearchContextItem[]>
    ) => {
      state.quickFiltersList = action.payload;
    },
    updateSelectedFilters: (
      state,
      action: PayloadAction<CombinedSelectedFilters[]>
    ) => {
      state.selectedFilters = action.payload;
    },
    setSearchResultLoadingByQuestion: (
      state,
      action: PayloadAction<SearchResultLoadingByQuestion>
    ) => {
      state.searchResultLoadingByQuestion = action.payload;
    },
    setIsArticleGeneratedByQuestion: (
      state,
      action: PayloadAction<ArticleGeneratedByQuestion>
    ) => {
      state.isArticleGeneratedByQuestion = action.payload;
    },
    updateFollowupsByQuestion: (
      state,
      action: PayloadAction<Record<string, NextStep[]>>
    ) => {
      state.followupsByQuestion = action.payload;
    },

    updateSkills: (state, action: PayloadAction<CommandItem[]>) => {
      state.skills = action.payload;
    }
  }
});

export const {
  updateSelectedSearchResult,
  updateSelectedSearchResultsQuestion,
  updateSelectedSearchResultsQuestionText,
  updateSearchCardVisibility,
  addSearchResults,
  updateSearchResults,
  updateSearchResultsExpanded,
  updateSearchResultsInfo,
  setIsPreviewDataLoadingState,
  setIsSearchFocused,
  setIsSearchLoading,
  setInitialSearchQuery,
  updateSearchQuery,
  updatePage,
  updateSearchFilter,
  updateSearchSuggestions,
  updateShowSuggestion,
  updateSearchFilterSubType,
  updateSortMode,
  updateHasLinkedEdge,
  resetSearch,
  updateAutoSearchActive,
  updateTotalSearchResults,
  updateSearchFilters,
  updateSearchResultVisibilityOnEmbeddedApp,
  updateAnswerId,
  resetRegenerateAnswerId,
  setSkipAutoProductFilters,
  updateRegenerateAnswerId,
  updateRegenerateQuestionIndex,
  updateShowBackButtonInPreview,
  updateSearchResultOffsets,
  updatePageHtml,
  updatePreviousPageHtml,
  updateFeedbackSubmittedIds,
  updateProductFilters,
  updateQuickFilters,
  updateMyProducts,
  setAnswerStreamingStarted,
  updateResultId,
  updateProductFiltersList,
  updateQuickFiltersList,
  updateSelectedFilters,
  setSearchResultsByQuestion,
  setSearchResultLoadingByQuestion,
  setIsArticleGeneratedByQuestion,
  updateFollowupsByQuestion,
  updateSearchResultByQuestion,
  updateSkills
} = searchSlice.actions;

export default searchSlice.reducer;
